import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import { AlertCircle, Check } from 'lucide-react'
import { useState } from 'react'

import type { FC } from 'react'

export interface TextInputProps {
	/** Unique ID for the input */
	id?: string
	/** Label text for the input */
	label: string
	/** Current value of the input */
	value: string
	/** Change handler for the input */
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
	/** Blur handler for the input */
	onBlur?: (e: any) => void
	/** Placeholder text for the input */
	placeholder?: string
	/** Input type */
	type?: string
	/** Whether the input is disabled */
	disabled?: boolean
	/** Whether the input is read-only */
	readOnly?: boolean
	/** Whether the input is required */
	required?: boolean
	/** Helper text displayed below the input */
	helperText?: string
	/** Text to display on hover, replaces helperText when hovering */
	hoverText?: string
	/** Whether to show the success indicator */
	showSuccess?: boolean
	/** Whether to show the loading indicator */
	showLoading?: boolean
	/** Error message to display */
	error?: string
	/** Additional CSS classes for the container */
	className?: string
	/** Additional CSS classes for the input */
	inputClassName?: string
}

export const TextInput: FC<TextInputProps> = ({
	id,
	label,
	value,
	onChange,
	onBlur,
	placeholder,
	type = 'text',
	disabled = false,
	readOnly = false,
	required = false,
	helperText,
	hoverText,
	showSuccess = false,
	showLoading = false,
	error,
	className = '',
	inputClassName = '',
}) => {
	const [isHovered, setIsHovered] = useState(false)
	const hasError = Boolean(error)

	const displayText = isHovered && hoverText ? hoverText : helperText

	return (
		<div
			className={cn('space-y-2', className)}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<Label htmlFor={id} className={cn(hasError && 'text-destructive')}>
				{label}
				{required && <span className="text-destructive ml-1">*</span>}
			</Label>
			<div className="relative">
				<div className="relative max-w-md">
					<Input
						id={id}
						type={type}
						value={value}
						onChange={onChange}
						onBlur={onBlur}
						placeholder={placeholder}
						disabled={disabled}
						readOnly={readOnly}
						className={cn(
							'pr-10',
							hasError && 'border-destructive focus-visible:ring-destructive',
							showSuccess && 'border-green-500 focus-visible:ring-green-500',
							inputClassName
						)}
					/>
					{showLoading && !hasError && (
						<Spinner
							className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground"
						/>
					)}
					{showSuccess && !hasError && !showLoading && (
						<Check className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
					)}
					{hasError && (
						<AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-destructive" />
					)}
				</div>

				{/* Reserved space for text - always takes up space */}
				<div className="h-5 relative">
					{/* Error text - highest priority */}
					<p
						className={cn(
							'absolute top-0 left-0 text-sm text-destructive transition-opacity duration-200',
							error ? 'opacity-100' : 'opacity-0'
						)}
					>
						{error || '\u00A0'} {/* Non-breaking space to maintain layout */}
					</p>

					{/* Helper/Hover text - only show when no error */}
					<p
						className={cn(
							'absolute top-0 left-0 text-sm text-muted-foreground transition-opacity duration-200',
							!error && displayText ? 'opacity-100' : 'opacity-0'
						)}
					>
						{displayText || '\u00A0'} {/* Non-breaking space to maintain layout */}
					</p>
				</div>
			</div>
		</div>
	)
}
